/**
 * The drag list's arithmetic (`D215`), kept out of the component so the Node
 * runner can pin it (`__tests__/drag-list-math.test.ts`). The deleted Expo
 * app's `drag-order.ts` had `dropIndex`; this is the same rule, measured once
 * when the row lifts rather than on every move.
 *
 * No React, no DOM.
 */

/** A press held this long on a row lifts it. Shorter, and it was a tap or a scroll. */
export const LIFT_HOLD_MS = 320;

/** A finger that moves further than this before the hold ends is scrolling. */
export const LIFT_SLOP_PX = 8;

/** One row as it stood when the drag began: its top and its height. */
export interface RowBox {
  readonly height: number;
  readonly top: number;
}

/**
 * Where the lifted row lands. `rows` are the boxes in list order, measured at
 * lift; `dy` is how far the finger has moved since. Going down, a row gives
 * up its place once the lifted row's BOTTOM edge passes its middle; going up,
 * once the lifted row's TOP edge does — the leading edge, so a row moves as
 * soon as the finger is half over it rather than a whole slot later.
 */
export function dropIndex(
  rows: readonly RowBox[],
  from: number,
  dy: number
): number {
  const lifted = rows[from];
  if (lifted === undefined) {
    return from;
  }
  const top = lifted.top + dy;
  const bottom = top + lifted.height;
  let to = from;
  for (let index = from + 1; index < rows.length; index += 1) {
    const row = rows[index] as RowBox;
    if (bottom > row.top + row.height / 2) {
      to = index;
    }
  }
  for (let index = from - 1; index >= 0; index -= 1) {
    const row = rows[index] as RowBox;
    if (top < row.top + row.height / 2) {
      to = index;
    }
  }
  return to;
}

/**
 * How far row `index` is pushed while the lifted row (`from`, `height` tall
 * plus the list's `gap`) is held over `to`: the rows between make room by
 * stepping one slot towards where it came from. The lifted row itself is
 * drawn under the finger instead, so it gets no shift here.
 */
export function shiftOf(
  index: number,
  from: number,
  to: number,
  slot: number
): number {
  if (index === from) {
    return 0;
  }
  if (from < to && index > from && index <= to) {
    return -slot;
  }
  if (to < from && index >= to && index < from) {
    return slot;
  }
  return 0;
}

/** `keys` with the one at `from` moved to `to`; the same array when nothing moves. */
export function moved<T>(
  keys: readonly T[],
  from: number,
  to: number
): readonly T[] {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= keys.length ||
    to >= keys.length
  ) {
    return keys;
  }
  const next = [...keys];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}
